/**
 * Telemetry transport — robust fetch wrapper for snapshot delivery.
 *
 * 11/02/2026 (Itération 92) — Retour user prod : « Plusieurs apprenants
 * jouent (Safari Mac) mais aucune donnée ne remonte ». Investigation :
 *
 * Cause racine identifiée : le hook `useTelemetryReporter` posait toutes
 * ses requêtes avec `fetch(..., { keepalive: true })`. Or Safari (≥ 13)
 * applique un quota cumulatif de **64 KB par session navigateur** pour
 * l'option `keepalive`. Chaque snapshot HST faisant ~2-5 KB, dès que
 * l'apprenant a envoyé ~15-20 snapshots dans sa session, Safari **rejette
 * silencieusement** tous les fetches keepalive suivants sans déclencher
 * d'erreur côté JS — c'est pour ça que la supervision ne voyait plus
 * de mises à jour pour les utilisateurs Safari longtemps après leur
 * première heure de jeu.
 *
 * Mitigations dans ce helper :
 *
 *   1. **Sans `keepalive`** par défaut pour les envois périodiques (le
 *      flag n'est utile QUE pour le tir final au `beforeunload`).
 *   2. **Retry exponentiel** sur erreur réseau / 5xx : 0s puis 2s puis 5s.
 *   3. **File d'attente persistante** : si tous les retries échouent, on
 *      stocke le snapshot dans localStorage (`hst-telemetry-queue`,
 *      max 10 entrées) pour retry au prochain interval / au rechargement.
 *   4. **Logs verbeux** : status, license_key (masquée), tentative, taille.
 *   5. **`sendBeacon`** utilisé en fallback final dans le handler
 *      `beforeunload` (déclaré par le hook lui-même).
 */

const QUEUE_KEY = 'hst-telemetry-queue';
const MAX_QUEUE = 10;
// 17/07/2026 — Statut de synchro exposé à l'UI (SaveGameModal) pour
// diagnostiquer sur place les apprenants « invisibles » en supervision.
const STATUS_KEY = 'hst-telemetry-status';

export function recordTelemetryStatus(patch) {
  try {
    const prev = JSON.parse(localStorage.getItem(STATUS_KEY) || '{}');
    localStorage.setItem(STATUS_KEY, JSON.stringify({ ...prev, ...patch }));
  } catch (_) { /* localStorage indisponible (mode privé) */ }
}

export function getTelemetryStatus() {
  try {
    return JSON.parse(localStorage.getItem(STATUS_KEY) || 'null');
  } catch (_) {
    return null;
  }
}
// 11/02/2026 — Exposé pour permettre aux tests d'override les délais à 0.
// En prod : [0, 2000, 5000] (3 tentatives, exponential backoff).
export const RETRY_DELAYS_MS = [0, 2000, 5000];
// Helper interne pour la pause entre les retries — facilement mockable.
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** Masque la clé pour les logs (XXXXX...XXXXX-XX). */
function maskKey(key) {
  if (!key || typeof key !== 'string') return '???';
  return key.length > 8
    ? `${key.slice(0, 6)}…${key.slice(-2)}`
    : key.slice(0, 4) + '…';
}

function loadQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (_) {
    return [];
  }
}

function saveQueue(queue) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue.slice(-MAX_QUEUE)));
  } catch (_) {
    /* localStorage plein ou disabled (mode privé Safari) — on ignore */
  }
}

function enqueueFailedSnapshot(snapshot) {
  const queue = loadQueue();
  queue.push({ snapshot, enqueuedAt: Date.now() });
  saveQueue(queue);
}

/**
 * Tente l'envoi d'un snapshot avec retry exponentiel. Retourne true en cas
 * de succès, false sinon. N'utilise PAS `keepalive` (cf. doc en-tête).
 */
async function postOnce(backend, snapshot, { signal } = {}) {
  const res = await fetch(`${backend}/api/telemetry/snapshot`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(snapshot),
    signal,
    // Pas de keepalive ici : usage périodique en foreground.
  });
  return res;
}

/**
 * Envoie un snapshot avec retry exponentiel + persistance en file en cas
 * d'échec définitif.
 */
export async function sendTelemetrySnapshot(backend, snapshot) {
  if (!backend) return { ok: false, reason: 'no_backend' };
  const license = snapshot?.license_key || '?';
  const sizeKB = (JSON.stringify(snapshot).length / 1024).toFixed(1);

  for (let attempt = 0; attempt < RETRY_DELAYS_MS.length; attempt++) {
    if (RETRY_DELAYS_MS[attempt] > 0) {
      await sleep(RETRY_DELAYS_MS[attempt]);
    }
    try {
      const res = await postOnce(backend, snapshot);
      if (res.ok) {
        // 17/07/2026 — le backend renvoie HTTP 200 même quand il REFUSE le
        // snapshot (clé inconnue / licence désactivée) pour ne pas casser
        // le jeu. On lit le corps pour détecter ce rejet silencieux et
        // l'exposer dans l'indicateur de synchro.
        let body = null;
        try { body = await res.json(); } catch (_) { /* corps vide */ }
        if (body && body.ok === false) {
          console.warn(`[telemetry] snapshot refusé par le serveur (license=${maskKey(license)}, reason=${body.reason || '?'})`);
          recordTelemetryStatus({ lastAttemptAt: Date.now(), lastResult: 'rejected', reason: body.reason || 'rejected' });
          return { ok: false, reason: body.reason || 'rejected' };
        }
        recordTelemetryStatus({ lastAttemptAt: Date.now(), lastSuccessAt: Date.now(), lastResult: 'ok', reason: null });
        if (attempt > 0) {
          console.info(`[telemetry] snapshot delivered after ${attempt} retry (license=${maskKey(license)}, size=${sizeKB}KB)`);
        }
        return { ok: true };
      }
      // 4xx → don't retry, the backend rejected the payload (bad license, etc.)
      if (res.status >= 400 && res.status < 500) {
        console.warn(`[telemetry] snapshot rejected (license=${maskKey(license)}, status=${res.status} ${res.statusText})`);
        recordTelemetryStatus({ lastAttemptAt: Date.now(), lastResult: 'rejected', reason: `http_${res.status}` });
        return { ok: false, reason: 'rejected', status: res.status };
      }
      console.warn(`[telemetry] snapshot 5xx (attempt ${attempt + 1}/${RETRY_DELAYS_MS.length}, license=${maskKey(license)}, status=${res.status})`);
    } catch (err) {
      const msg = err?.message || String(err);
      console.warn(`[telemetry] snapshot network error (attempt ${attempt + 1}/${RETRY_DELAYS_MS.length}, license=${maskKey(license)}): ${msg}`);
    }
  }
  // Tous les retries ont échoué → on persiste pour retry plus tard.
  console.warn(`[telemetry] snapshot FAILED after ${RETRY_DELAYS_MS.length} attempts — queued for later (license=${maskKey(license)}, size=${sizeKB}KB)`);
  recordTelemetryStatus({ lastAttemptAt: Date.now(), lastResult: 'queued', reason: 'network' });
  enqueueFailedSnapshot(snapshot);
  return { ok: false, reason: 'queued' };
}

/**
 * Vide la file d'attente persistée. Appelé périodiquement par le hook
 * pour rattraper les snapshots des sessions précédentes (ex. apprenant
 * qui éteint son Mac sans fermer Safari proprement).
 */
export async function flushTelemetryQueue(backend) {
  if (!backend) return { sent: 0, remaining: 0 };
  const queue = loadQueue();
  if (queue.length === 0) return { sent: 0, remaining: 0 };
  console.info(`[telemetry] flushing ${queue.length} queued snapshot(s)…`);
  const remaining = [];
  let sent = 0;
  for (const entry of queue) {
    try {
      const res = await postOnce(backend, entry.snapshot);
      if (res.ok) {
        sent += 1;
      } else if (res.status >= 400 && res.status < 500) {
        // Bad license → drop the entry definitively.
        // (e.g. license deactivated since the snapshot was queued)
      } else {
        remaining.push(entry);
      }
    } catch (_) {
      remaining.push(entry);
    }
  }
  saveQueue(remaining);
  if (sent > 0) {
    console.info(`[telemetry] flushed ${sent} queued snapshot(s), ${remaining.length} still pending`);
  }
  return { sent, remaining: remaining.length };
}

/**
 * Beacon fallback for `beforeunload` events. `sendBeacon` is the ONLY
 * reliable transport when the page is unloading — fetch (with or without
 * keepalive) is unreliable on Safari at unload time. Returns true if the
 * browser accepted the beacon (no guarantee it lands, but at least it's
 * queued by the platform).
 */
export function sendTelemetryBeacon(backend, snapshot) {
  if (!backend || typeof navigator === 'undefined' || !navigator.sendBeacon) {
    return false;
  }
  try {
    const blob = new Blob([JSON.stringify(snapshot)], { type: 'application/json' });
    return navigator.sendBeacon(`${backend}/api/telemetry/snapshot`, blob);
  } catch (err) {
    console.warn('[telemetry] beacon failed:', err?.message || err);
    return false;
  }
}
